import { useCallback, useEffect, useState } from 'react';
/** Default radius applied when a policy has geofencing on but no radius set.
 *  Must match the policy form default and the backend fallback. */
export const DEFAULT_GEOFENCE_RADIUS_M = 100;
const SCOPED_FLAG_KEY = {
    timeclock: 'enable_geofencing_timeclock',
    work_orders: 'enable_geofencing_work_orders',
};
/**
 * Resolve a raw session-policy payload into a GeofenceConfig for one scope.
 * Returns null when geofencing is not enforceable for that scope (master off,
 * scoped flag off, or no coordinates configured — the server also fails open
 * without coordinates).
 */
export function resolveGeofenceConfig(policy, scope) {
    if (!policy)
        return null;
    const master = !!policy.enable_geofencing;
    const scoped = policy[SCOPED_FLAG_KEY[scope]];
    const enabled = master && (scoped ?? master);
    if (!enabled || policy.geofence_latitude == null || policy.geofence_longitude == null) {
        return null;
    }
    return {
        enabled: true,
        latitude: policy.geofence_latitude,
        longitude: policy.geofence_longitude,
        radiusMeters: policy.geofence_radius_meters || DEFAULT_GEOFENCE_RADIUS_M,
    };
}
/**
 * Fetches the effective session policy for a location and derives the
 * geofence preflight config for one scope. Platform components own the
 * refresh triggers (web: tab visibility; native: AppState foreground) and
 * call `refresh` from them.
 */
export function useSessionPolicy(client, accountId, locationId, scope, options) {
    const [policy, setPolicy] = useState(null);
    const bypass = options?.bypassGeofence ?? false;
    const refresh = useCallback(async () => {
        if (!accountId || !locationId) {
            setPolicy(null);
            return;
        }
        try {
            const res = await client.get(`/accounts/${accountId}/locations/${locationId}/session-policy`);
            setPolicy(res && (res.success === undefined || res.success) && res.data ? res.data : null);
        }
        catch {
            setPolicy(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, locationId]);
    useEffect(() => {
        refresh();
    }, [refresh]);
    const geofenceConfig = bypass ? null : resolveGeofenceConfig(policy, scope);
    return { policy, geofenceConfig, refresh };
}
